import { useState } from 'react';

import { format, isValid } from 'date-fns';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Checkbox,
  DateValue,
  Modal,
  ModalOverlayProps,
  UNSTABLE_DatePicker as DatePicker,
} from '@cfa/react-core';

import { isValidYear } from '@/utils/date';
import { cn } from '@/utils/tailwind';

interface DueDatePopUpProps extends Omit<ModalOverlayProps, 'children'> {
  /**
   * Invoked when the user saves their selection.
   * Receives the ISO 8601 formatted date string (e.g., "2025-03-15T00:00:00.000Z")
   * or `undefined` if "No due date required" is checked.
   */
  onSave?: (date: string | undefined) => void;
}

/**
 * A modal dialog component for selecting or clearing a due date.
 *
 * Provides a date picker interface that allows users to either:
 * - Select a specific due date using the date picker
 * - Opt out of a due date by checking the "No due date required" checkbox
 *
 * @example
 * <DueDatePopUp
 *   onOpenChange={setIsOpen}
 *   onSave={(date) => {
 *     if (date) {
 *       console.log('Due date set:', date);
 *     } else {
 *       console.log('No due date required');
 *     }
 *   }}
 * />
 */
const DueDatePopUp: React.FC<DueDatePopUpProps> = ({ onSave, ...props }) => {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState<string>();
  const [noDueDateRequired, setNoDueDateRequired] = useState(false);
  const [disableDatePicker, setDisableDatePicker] = useState(false);

  const handleDateChange = (date: DateValue | null) => {
    const year = date?.year;

    // we only receive a date from the Date Picker
    // when there is at least 1 number typed into
    // the year section, we check to make sure the year is 4 digits here
    if (!year || !isValidYear(year)) {
      setSelectedDate(undefined);
      return;
    }

    const formattedDate = format(
      date.toDate(Intl.DateTimeFormat().resolvedOptions().timeZone),
      "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'",
    );

    // checks to make sure date is a proper date format
    if (isValid(new Date(formattedDate))) {
      setSelectedDate(formattedDate);
    }
  };

  return (
    <Modal.Overlay {...props} className={cn('z-2000', props.className)}>
      <Modal.Dialog>
        <Modal.Title className="pb-5 text-center">
          {t('TrainingPlans.accountability.setDueDate')}
        </Modal.Title>
        <Modal.Content>
          <div className="flex flex-row justify-center">
            <div className="flex flex-col items-start justify-start text-left">
              <DatePicker
                data-testid="DatePicker"
                isDisabled={disableDatePicker}
                isRequired={!noDueDateRequired}
                isInvalid={noDueDateRequired ? false : undefined}
                label={t('TrainingPlans.accountability.enterDueDate')}
                onChange={handleDateChange}
                shouldForceLeadingZeros={false}
              />
              <Checkbox
                data-testid="DueDateCheckbox"
                onChange={() => {
                  setNoDueDateRequired(prev => !prev);
                  setDisableDatePicker(prev => !prev);
                }}
                isSelected={noDueDateRequired}
                className="text-variant-body1! mt-5"
              >
                {t('TrainingPlans.accountability.due.noDueDate')}
              </Checkbox>
            </div>
          </div>
        </Modal.Content>
        <Modal.Footer className="mt-8">
          <Button
            color="secondary"
            data-testid="ButtonSave"
            isDisabled={!selectedDate && !noDueDateRequired}
            id="SaveDueDate"
            onPress={() =>
              onSave?.(noDueDateRequired ? undefined : selectedDate)
            }
          >
            {t('Button.save')}
          </Button>
        </Modal.Footer>
      </Modal.Dialog>
    </Modal.Overlay>
  );
};

export default DueDatePopUp;
